import React, { useState, useEffect } from 'react';
import { getSmtpSettings, updateSmtpSettings } from '../../api/smtp';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { useLoader } from '../../context/LoaderContext';

const SmtpSettings = () => {
    const { showLoader, hideLoader } = useLoader();
    const [formData, setFormData] = useState({
        host: '',
        port: 0,
        userName: '',
        password: '',
        enableSsl: false,
        language: '',
        app_id: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        showLoader();
        try {
            const res = await getSmtpSettings();
            // Handle both { status: true, data: { ... } } and direct object { ... }
            const data = (res?.status && res?.data) ? res.data : (res?.isTrue && res?.data ? res.data : res);

            if (data) {
                setFormData({
                    host: data.host || '',
                    port: data.port || 0,
                    userName: data.userName || '',
                    password: data.password || '',
                    enableSsl: data.enableSsl || false,
                    language: data.language || '',
                    app_id: data.app_id || ''
                });
            }
        } catch (error) {
            console.error("SMTP ayarları yüklenemedi:", error);
            toast.error(error.message || "SMTP ayarları yüklenemedi.");
        } finally {
            hideLoader();
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoader();
        setLoading(true);

        try {
            await updateSmtpSettings(formData);
            toast.success("SMTP ayarları başarıyla güncellendi.");
        } catch (error) {
            console.error("Güncelleme hatası:", error);
            toast.error(error.response?.data?.message || "SMTP ayarları güncellenirken bir hata oluştu.");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">SMTP Ayarları</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        {/* Host */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">SMTP Host</label>
                            <div className="input-group">
                                <span className="input-group-text bg-base border-end-0">
                                    <Icon icon="heroicons:server" className="text-xl" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control radius-8 border-start-0"
                                    name="host"
                                    placeholder="e.g. smtp.gmail.com"
                                    value={formData.host}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Port */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">Port</label>
                            <div className="input-group">
                                <span className="input-group-text bg-base border-end-0">
                                    <Icon icon="heroicons:hashtag" className="text-xl" />
                                </span>
                                <input
                                    type="number"
                                    className="form-control radius-8 border-start-0"
                                    name="port"
                                    placeholder="587"
                                    value={formData.port}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* User Name */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">Kullanıcı Adı</label>
                            <div className="input-group">
                                <span className="input-group-text bg-base border-end-0">
                                    <Icon icon="heroicons:user" className="text-xl" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control radius-8 border-start-0"
                                    name="userName"
                                    placeholder="example@mail.com"
                                    value={formData.userName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">Şifre</label>
                            <div className="input-group">
                                <span className="input-group-text bg-base border-end-0">
                                    <Icon icon="heroicons:lock-closed" className="text-xl" />
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control radius-8 border-start-0 border-end-0"
                                    name="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                                <span
                                    className="input-group-text bg-base border-start-0 cursor-pointer"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Icon
                                        icon={showPassword ? "heroicons:eye-slash" : "heroicons:eye"}
                                        className="text-xl text-secondary-light"
                                    />
                                </span>
                            </div>
                        </div>

                        {/* App ID */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">App ID</label>
                            <div className="input-group">
                                <span className="input-group-text bg-base border-end-0">
                                    <Icon icon="heroicons:identification" className="text-xl" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control radius-8 border-start-0"
                                    name="app_id"
                                    placeholder="Uygulama Kimliği"
                                    value={formData.app_id}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        {/* Language */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light">Dil (Language)</label>
                            <select
                                className="form-select radius-8"
                                name="language"
                                value={formData.language}
                                onChange={handleChange}
                            >
                                <option value="">Dil Seçin</option>
                                <option value="tr">Türkçe (tr)</option>
                                <option value="en">English (en)</option>
                            </select>
                        </div>

                        {/* SSL Enable */}
                        <div className="col-12">
                            <div className="form-check form-switch d-flex align-items-center gap-3">
                                <input
                                    className="form-check-input cursor-pointer"
                                    type="checkbox"
                                    role="switch"
                                    id="enableSsl"
                                    name="enableSsl"
                                    checked={formData.enableSsl}
                                    onChange={handleChange}
                                    style={{ width: '40px', height: '20px' }}
                                />
                                <label className="form-check-label fw-semibold text-primary-light mb-0" htmlFor="enableSsl">
                                    SSL Kullan (Enable SSL)
                                </label>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="col-12 mt-32">
                            <button
                                type="submit"
                                className="btn btn-primary-600 radius-8 px-32 py-12 d-flex align-items-center gap-2 fw-semibold"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Icon icon="line-md:loading-loop" className="text-xl" /> Güncelleniyor...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="heroicons:check-circle" className="text-xl" /> Ayarları Kaydet
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SmtpSettings;
